import { createFetchRequester } from '@algolia/requester-fetch';
import { algoliasearch, SearchClient } from 'algoliasearch';
import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { normalizePoboxRecord } from './poboxAddress';
import {
  AddressValidationSource,
  formatAlgoliaFilterValue,
  normalizeAddressValidationSource,
  normalizeTextForMatching,
} from '../utils/addressValidationSource';
import { CountryCode } from '../utils/multiTenancyHeaders';
import { ShopifyTokenGenerator, getShopifyAuthHeader } from '../utils/shopifySessionTokens';
import {
  AlgoliaLocationData,
  PoboxValidationResponse,
  QasAddressResponse,
  QasSearchResponseMessage,
  ValidatePoboxAddressParams,
} from './types';

type QasCountryCode = 'AUE' | 'NZD';

type ConstructorParams =
  | {
      url: string;
      isoCountryCode: CountryCode;
      shopifyTokenGenerator: ShopifyTokenGenerator;
      accessToken?: never;
      addressValidationApiSource?: AddressValidationSource;
      algoliaAppId?: string;
      algoliaSearchApiKey?: string;
      algoliaPoboxIndex?: string;
    }
  | {
      url: string;
      isoCountryCode: CountryCode;
      accessToken: string;
      shopifyTokenGenerator?: never;
      addressValidationApiSource?: AddressValidationSource;
      algoliaAppId?: string;
      algoliaSearchApiKey?: string;
      algoliaPoboxIndex?: string;
    };

export class AddressValidationClient extends ApiClient {
  private readonly _url: string;
  private readonly _accessToken?: string;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;
  private readonly _isoCountryCode: CountryCode;
  private readonly _qasCountryCode: QasCountryCode;
  private readonly _source: AddressValidationSource;
  private readonly _algoliaClient?: SearchClient;
  private readonly _algoliaPoboxIndex?: string;

  constructor({
    url,
    isoCountryCode,
    accessToken,
    shopifyTokenGenerator,
    addressValidationApiSource,
    algoliaAppId,
    algoliaSearchApiKey,
    algoliaPoboxIndex,
  }: ConstructorParams) {
    super();
    this._url = url;
    this._accessToken = accessToken;
    this._isoCountryCode = isoCountryCode;
    this._qasCountryCode = isoCountryCode === 'NZ' ? 'NZD' : 'AUE';
    this._shopifyTokenGenerator = shopifyTokenGenerator;
    this._source = normalizeAddressValidationSource(addressValidationApiSource);
    this._algoliaPoboxIndex = algoliaPoboxIndex;
    this._algoliaClient =
      algoliaAppId && algoliaSearchApiKey && algoliaPoboxIndex
        ? algoliasearch(algoliaAppId, algoliaSearchApiKey, { requester: createFetchRequester() })
        : undefined;
  }

  generateHeaders = async () => {
    const isInCheckoutExtensibility = this._shopifyTokenGenerator !== undefined;
    let userValidationHeaders: Record<string, string> = {};
    if (isInCheckoutExtensibility) {
      userValidationHeaders = (await getShopifyAuthHeader(this._shopifyTokenGenerator))!;
      return { ...super.requestHeaders, ...userValidationHeaders };
    } else if (this._accessToken) {
      return {
        ...super.requestHeaders,
        'x-jbg-api-key': this._accessToken,
      };
    }
  };

  getAddressSearch = async (
    query: string,
    abortSignal: AbortSignal
  ): Promise<ApiResponse<Array<QasSearchResponseMessage>>> => {
    const headers = await this.generateHeaders();
    const sourceParam = `&source=${encodeURIComponent(this._source)}`;
    const path = `${this._url}/search?countryCode=${this._qasCountryCode}&address=${encodeURIComponent(
      query
    )}${sourceParam}`;

    const { data, statusCode, error } = await this._fetch<any>({
      path,
      method: 'GET',
      abortSignal,
      headers,
    });

    if (error) {
      return { error, statusCode };
    }

    return { data: data.message, error: error, statusCode };
  };

  getAddressDetailById = async (id: string): Promise<ApiResponse<QasAddressResponse>> => {
    const headers = await this.generateHeaders();
    const sourceParam = `&source=${encodeURIComponent(this._source)}`;
    const path = `${this._url}/address?countryCode=${this._qasCountryCode}&id=${btoa(id)}${sourceParam}`;

    const { data, statusCode, error } = await this._fetch<QasAddressResponse>({
      path,
      method: 'GET',
      headers,
    });

    if (error || !data) {
      return { error, statusCode };
    }

    return {
      data: {
        ...data,
        countryCode: this._isoCountryCode,

        message: {
          ...data.message,
          //For NZ, always use CityState as the Suburb value to satisfy Shopify form validation requirements
          Suburb: this._isoCountryCode === 'NZ' ? data.message.CityState : data.message.Suburb,
          Region: this._isoCountryCode === 'NZ' ? data.message.Region : '',
        },
      },
      statusCode,
    };
  };

  validatePoboxAddress = async ({
    postcode,
    state,
    hitsPerPage = 20,
  }: ValidatePoboxAddressParams): Promise<ApiResponse<PoboxValidationResponse>> => {
    if (!this._algoliaClient || !this._algoliaPoboxIndex) {
      return { error: { message: 'Algolia PO Box index not configured.' }, statusCode: 403 };
    }

    const normalizedPostcode = postcode ? String(postcode).trim() : '';
    const resolvedFilters = normalizedPostcode ? `postcode:${formatAlgoliaFilterValue(normalizedPostcode)}` : undefined;
    try {
      const data = await this._algoliaClient.searchSingleIndex<AlgoliaLocationData>({
        indexName: this._algoliaPoboxIndex,
        searchParams: {
          hitsPerPage,
          ...(resolvedFilters ? { filters: resolvedFilters } : {}),
        },
      });
      const normalizedState = normalizeTextForMatching(state);
      const normalizedHits = data.hits.map(hit => normalizePoboxRecord(hit));
      // For NZ, skip state filtering: Algolia 'state' contains city names (e.g., "Hamilton"),
      // but checkout provinceCode contains NZ region names (e.g., "Canterbury")
      const filteredHits =
        this._isoCountryCode === 'NZ'
          ? normalizedHits
          : normalizedState
            ? normalizedHits.filter(hit => normalizeTextForMatching(hit.State) === normalizedState)
            : normalizedHits;

      return {
        data: {
          ...data,
          hits: filteredHits,
          nbHits: filteredHits.length,
        },
        statusCode: 200,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error validating PO Box address.';
      return { error: { message }, statusCode: 500 };
    }
  };
}
